import { marked } from "https://cdn.jsdelivr.net/npm/marked/lib/marked.esm.js";
import { DirectToolGroup } from "./DirectToolGroup.js";
import { OutputPresentationCard } from "./OutputPresentationCard.js";

let _hljsPromise = null;
function _loadHljs() {
    if (window.hljs) return Promise.resolve(window.hljs);
    if (_hljsPromise) return _hljsPromise;

    _hljsPromise = new Promise((resolve) => {
        const themeId = 'chat-hljs-theme';
        if (!document.getElementById(themeId)) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.id = themeId;
            link.href = 'https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.9.0/styles/atom-one-dark.min.css';
            document.head.appendChild(link);
        }

        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.9.0/highlight.min.js';
        script.onload = () => resolve(window.hljs);
        script.onerror = () => resolve(null);
        document.head.appendChild(script);
    });
    return _hljsPromise;
}

export class ChatMessage {
    static render(msg, context) {
        let node;
        switch (msg.role) {
            case 'turn':         node = ChatMessage._renderTurn(msg, context); break;
            case 'user':         node = ChatMessage._renderBubble(msg, context, 'user'); break;
            case 'assistant':    node = ChatMessage._renderBubble(msg, context, 'assistant'); break;
            case 'system':       node = ChatMessage._renderSystem(msg, context); break;
            case 'tool_group':   node = ChatMessage._renderToolGroup(msg, context); break;
            case 'tool_progress':node = ChatMessage._renderSkill(msg, context); break;
            case 'presentation': node = ChatMessage._renderPresentation(msg, context); break;
            case 'event':        node = ChatMessage._renderEvent(msg, context); break;
            default:             node = ChatMessage._renderBubble(msg, context, 'assistant'); break;
        }
        if (node && msg._id) {
            node.setAttribute('data-message-id', msg._id.toString());
        }
        return node;
    }

    static _renderTurn(msg, context) {
        const wrapper = document.createElement('div');
        wrapper.className = 'ws-turn-wrapper';

        const userMessage = msg.metadata?.userMessage || { role: 'user', content: msg.content, metadata: msg.metadata };
        wrapper.appendChild(ChatMessage._renderBubble(userMessage, context, 'user'));

        const blocks = msg.metadata?.blocks;
        if (Array.isArray(blocks) && blocks.length > 0) {
            for (const block of blocks) {
                if (block.type === 'assistant') {
                    wrapper.appendChild(ChatMessage._renderBubble(block.message, context, 'assistant'));
                } else if (block.type === 'tools') {
                    wrapper.appendChild(ChatMessage._renderInlineToolGroup({ ...msg, metadata: { tools: block.tools } }, context));
                } else if (block.type === 'presentation') {
                    wrapper.appendChild(ChatMessage._renderInlinePresentation(msg, block.data, context));
                }
            }
        } else {
            const tools = Array.isArray(msg.metadata?.tools) ? msg.metadata.tools : [];
            if (tools.length > 0) {
                wrapper.appendChild(ChatMessage._renderInlineToolGroup({ ...msg, metadata: { tools } }, context));
            }

            const assistantMessages = Array.isArray(msg.metadata?.assistantMessages) ? msg.metadata.assistantMessages : [];
            for (const assistantMessage of assistantMessages) {
                wrapper.appendChild(ChatMessage._renderBubble(assistantMessage, context, 'assistant'));
            }

            const presentations = Array.isArray(msg.metadata?.presentations) ? msg.metadata.presentations : [];
            for (const presentation of presentations) {
                wrapper.appendChild(ChatMessage._renderInlinePresentation(msg, presentation, context));
            }
        }

        return wrapper;
    }

    static _renderBubble(msg, context, side) {
        const wrapper = document.createElement('div');
        wrapper.className = `ws-msg-wrapper ${side}`;
        if (msg.isEphemeral) wrapper.classList.add('ephemeral-transcript');

        const bubble = document.createElement('div');
        bubble.className = 'ws-msg-bubble';

        // Add display name for group chats if needed (future Phase 4)
        if (context.isGroup && side !== 'user' && msg.senderId) {
            const nameEl = document.createElement('div');
            nameEl.className = 'ws-msg-sender-name';
            nameEl.style.fontSize = '0.75rem';
            nameEl.style.opacity = '0.7';
            nameEl.style.marginBottom = '2px';
            nameEl.textContent = msg.senderId;
            wrapper.appendChild(nameEl);
        }

        if (side === 'assistant') {
            const rawContent = msg.content || '';
            const TRUNCATE_LIMIT = 50000;
            const isTruncated = rawContent.length > TRUNCATE_LIMIT;
            
            const displayContent = isTruncated 
                ? rawContent.slice(0, TRUNCATE_LIMIT) + '\n\n*... [Content truncated for performance] ...*' 
                : rawContent;

            bubble.innerHTML = marked.parse(displayContent);
            
            // Apply syntax highlighting to code blocks
            _loadHljs().then(hljs => {
                if (hljs) {
                    bubble.querySelectorAll('pre code').forEach(code => {
                        hljs.highlightElement(code);
                    });
                }
            });

            if (isTruncated) {
                const expandLink = document.createElement('div');
                expandLink.className = 'ws-msg-truncate-notice';
                expandLink.innerHTML = `<button class="btn btn-sm btn-outline-secondary mt-3"><i class="bi bi-arrows-expand me-1"></i> Render Full Message (${(rawContent.length / 1024).toFixed(1)} KB)</button>`;
                
                expandLink.querySelector('button').addEventListener('click', (e) => {
                    e.stopPropagation();
                    expandLink.remove();
                    bubble.innerHTML = marked.parse(rawContent);
                    
                    _loadHljs().then(hljs => {
                        if (hljs) {
                            bubble.querySelectorAll('pre code').forEach(code => {
                                hljs.highlightElement(code);
                            });
                        }
                    });
                    
                });
                bubble.appendChild(expandLink);
            }
        } else {
            const safeText = ChatMessage._escapeHtml(msg.content || '');
            const urlRegex = /(https?:\/\/[^\s]+)/g;
            bubble.innerHTML = safeText.replace(urlRegex, '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-white text-decoration-underline">$1</a>');
            
            const COLLAPSE_THRESHOLD = 400;
            if ((msg.content || '').length > COLLAPSE_THRESHOLD) {
                bubble.classList.add('collapsible', 'collapsed');
                const expandBtn = document.createElement('button');
                expandBtn.className = 'ws-expand-btn';
                expandBtn.textContent = 'Show more ▾';
                expandBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    const collapsed = bubble.classList.toggle('collapsed');
                    expandBtn.textContent = collapsed ? 'Show more ▾' : 'Show less ▴';
                });
                wrapper.appendChild(expandBtn);
            }
        }

        if (msg.attachments?.length > 0) {
            const attContainer = document.createElement('div');
            attContainer.className = 'mt-2 d-flex flex-wrap gap-1';
            msg.attachments.forEach(att => {
                attContainer.innerHTML += `<span class="badge bg-light text-dark border"><i class="bi bi-paperclip"></i> ${att.fileName}</span>`;
            });
            bubble.appendChild(attContainer);
        }

        wrapper.appendChild(bubble);
        if (side === 'assistant') {
            if (Array.isArray(msg.metadata?.tools) && msg.metadata.tools.length > 0) {
                wrapper.appendChild(ChatMessage._renderInlineToolGroup(msg, context));
            }
            if (Array.isArray(msg.metadata?.presentations) && msg.metadata.presentations.length > 0) {
                for (const presentation of msg.metadata.presentations) {
                    wrapper.appendChild(ChatMessage._renderInlinePresentation(msg, presentation, context));
                }
            }
            wrapper.appendChild(ChatMessage._renderActionRow(msg, context, msg.content || ''));
        }
        return wrapper;
    }

    static _renderInlineToolGroup(msg, context) {
        const wrapper = document.createElement('div');
        wrapper.className = 'ws-turn-artifact w-100';
        const group = new DirectToolGroup({
            instanceId: `dtg-inline-${msg._id || Date.now()}-${Math.random().toString(36).slice(2)}`,
            appClient: context.appClient,
            workspace: context.workspace,
            tools: msg.metadata?.tools || [],
            isOpen: false,
            threadId: context.workspace.currentThreadId,
        });
        group.render(wrapper);
        group.markComplete();
        return wrapper;
    }

    static _renderInlinePresentation(msg, presentation, context) {
        const wrapper = document.createElement('div');
        wrapper.className = 'ws-turn-artifact w-100';
        const card = new OutputPresentationCard({
            instanceId: `opc-inline-${msg._id || Date.now()}-${Math.random().toString(36).slice(2)}`,
            appClient: context.appClient,
            workspace: context.workspace,
            ...presentation,
            agentName: presentation?.agentName || context.workspace.currentAgentName,
            threadId: context.workspace.currentThreadId,
            projectId: context.workspace.currentProjectId,
        });
        card.render(wrapper);
        return wrapper;
    }

    static _renderActionRow(msg, context, rawContent) {
        const row = document.createElement('div');
        row.className = 'ws-msg-actions d-flex gap-1 mt-1 ms-1';

        const copyBtn = document.createElement('button');
        copyBtn.className = 'btn btn-sm btn-link text-muted p-1';
        copyBtn.title = 'Copy';
        copyBtn.innerHTML = '<i class="bi bi-clipboard"></i>';
        copyBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            navigator.clipboard.writeText(rawContent).then(() => {
                copyBtn.innerHTML = '<i class="bi bi-clipboard-check text-success"></i>';
                setTimeout(() => { copyBtn.innerHTML = '<i class="bi bi-clipboard"></i>'; }, 1500);
            }).catch(() => { });
        });

        row.appendChild(copyBtn);

        const disableTelemetry = context.workspace?.disableTelemetry ?? true;
        if (!disableTelemetry) {
            const statsBtn = document.createElement('button');
            statsBtn.className = 'btn btn-sm btn-link text-muted p-1';
            statsBtn.title = 'Statistics';
            statsBtn.innerHTML = '<i class="bi bi-bar-chart"></i>';
            statsBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                ChatMessage._showStatsOverlay(msg, context);
            });
            row.appendChild(statsBtn);
        }

        return row;
    }

    static async _showStatsOverlay(msg, context) {
        const t = (document.documentElement.getAttribute('data-bs-theme') || '').toLowerCase();
        const isDark = /dark|noir|midnight|ocean|forest|vibrant|agentverse-dark/.test(t);
        const closeBtnClass = isDark ? 'btn-close btn-close-white' : 'btn-close';

        const modal = document.createElement('div');
        modal.className = 'modal fade show message-statistics-modal';
        modal.style.display = 'block';
        modal.tabIndex = -1;
        modal.innerHTML = `
            <div class="modal-dialog modal-lg modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Message Statistics</h5>
                        <button type="button" class="${closeBtnClass}" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <ul class="nav nav-tabs mb-3">
                            <li class="nav-item"><button class="nav-link active" data-tab="turn">Turn</button></li>
                            <li class="nav-item"><button class="nav-link" data-tab="chat">Chat</button></li>
                        </ul>
                        <div class="ws-stats-content"><div class="text-muted small">Loading...</div></div>
                    </div>
                </div>
            </div>
        `;
        const backdrop = document.createElement('div');
        backdrop.className = 'modal-backdrop fade show';

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                close();
            }
        };
        document.addEventListener('keydown', handleKeyDown);

        const close = () => {
            document.removeEventListener('keydown', handleKeyDown);
            modal.remove();
            backdrop.remove();
        };
        modal.querySelector('.btn-close').addEventListener('click', close);
        backdrop.addEventListener('click', close);
        document.body.appendChild(backdrop);
        document.body.appendChild(modal);

        const content = modal.querySelector('.ws-stats-content');
        const state = { turn: null, chat: null };
        // Self-reliant accordion toggler
        modal.addEventListener('click', async (e) => {
            const btn = e.target.closest('[data-bs-toggle="collapse"]');
            if (btn) {
                e.preventDefault();
                e.stopPropagation();
                const targetId = btn.getAttribute('data-bs-target');
                const target = modal.querySelector(targetId);
                if (target) {
                    const isCollapsed = target.classList.toggle('show');
                    btn.classList.toggle('collapsed', !isCollapsed);

                    // If expanded, load full trace data on demand
                    if (isCollapsed) {
                        const item = btn.closest('.accordion-item');
                        const traceId = item?.getAttribute('data-trace-id');
                        const loadingEl = target.querySelector('.step-details-loading');
                        const contentEl = target.querySelector('.step-details-content');

                        if (traceId && contentEl && contentEl.innerHTML === '') {
                            try {
                                const fullTrace = await context.appClient.rpcClient.call('AITelemetryRpc.getTrace', { traceId });
                                if (fullTrace) {
                                    const stepCost = ChatMessage._traceCost(fullTrace);
                                    const stepCostStr = ChatMessage._formatCost(stepCost);
                                    const stepUsage = ChatMessage._usageBreakdown(fullTrace.usage);
                                    const stepDuration = fullTrace.durationMs || 0;
                                    const stepModel = fullTrace.modelIdentifier || 'unknown';
                                    const stepProvider = fullTrace.providerName || 'unknown';

                                    // Surface prompt blueprint and context loaded info (previously only in MagePanel)
                                    const bp = fullTrace.promptBlueprint;
                                    let blueprintHtml = '';
                                    if (bp) {
                                        const sectionsConfig = bp.dynamicContext?.sections || {};
                                        const sectionsLoaded = fullTrace.contextAssembly?.sections || {};
                                        const allSectionIds = Array.from(new Set([
                                            ...Object.keys(sectionsConfig),
                                            ...Object.keys(sectionsLoaded)
                                        ]));

                                        let sectionsHtml = '';
                                        if (allSectionIds.length > 0) {
                                            sectionsHtml = allSectionIds.map(sectionId => {
                                                const config = sectionsConfig[sectionId] || {};
                                                const loaded = sectionsLoaded[sectionId] || {};
                                                let statusStr = '';
                                                if (loaded.error) {
                                                    statusStr = `<span class="text-danger">Error: ${ChatMessage._escapeHtml(loaded.error)}</span>`;
                                                } else if (loaded.unavailable) {
                                                    statusStr = '<span class="text-warning">Unavailable</span>';
                                                } else if (loaded.digest) {
                                                    const shortDigest = loaded.digest.startsWith('sha256:') ? loaded.digest.slice(7, 20) + '…' : loaded.digest.slice(0, 12) + '…';
                                                    const tkns = loaded.tokenEstimate != null ? `${loaded.tokenEstimate} tkns` : '—';
                                                    const trunc = loaded.truncated ? ' <span class="badge bg-danger" style="font-size:var(--ab-text-2xs);">truncated</span>' : '';
                                                    statusStr = `<span class="text-success">${tkns} (${shortDigest})${trunc}</span>`;
                                                } else {
                                                    statusStr = '<span class="text-muted">Not evaluated</span>';
                                                }
                                                return `
                                                    <div class="mb-1 p-2 bg-body-secondary border rounded" style="font-size: var(--ab-text-xs); font-family: monospace; border-radius: var(--ab-radius-xs);">
                                                        <div class="d-flex justify-content-between">
                                                            <strong>${ChatMessage._escapeHtml(sectionId)}</strong>
                                                            <span class="text-muted">${ChatMessage._escapeHtml(config.surface || loaded.surface || '—')}</span>
                                                        </div>
                                                        <div class="text-secondary mt-1">Status: ${statusStr}</div>
                                                    </div>
                                                `;
                                            }).join('');
                                        }

                                        blueprintHtml = `
                                            <details class="mt-2">
                                                <summary class="text-info small cursor-pointer mb-2" style="font-weight: 500;"><i class="bi bi-info-circle me-1"></i>Prompt Blueprint & Context</summary>
                                                <div class="border rounded p-2 bg-body-tertiary mb-2" style="font-size: var(--ab-text-xs);">
                                                    <div class="mb-1"><strong>Base Prompt:</strong> <code>${ChatMessage._escapeHtml(bp.baseFile || '—')}</code></div>
                                                    ${bp.includedPartials?.length ? `<div class="mb-1"><strong>Included Partials:</strong> ${bp.includedPartials.map(p => `<span class="badge bg-secondary-subtle text-secondary-emphasis me-1" style="font-size:var(--ab-text-2xs);">${ChatMessage._escapeHtml(p)}</span>`).join('')}</div>` : ''}
                                                    ${fullTrace.contextAssembly?.contextKey ? `<div class="mb-1"><strong>Context Key:</strong> <code>${ChatMessage._escapeHtml(fullTrace.contextAssembly.contextKey)}</code></div>` : ''}
                                                    ${sectionsHtml ? `<div class="mt-2"><strong>Context Sections Loaded:</strong><div class="mt-1">${sectionsHtml}</div></div>` : ''}
                                                </div>
                                            </details>
                                        `;
                                    }

                                    contentEl.innerHTML = `
                                        <table class="table table-sm table-borderless small mb-2 text-body">
                                            <tbody>
                                                <tr><td class="text-muted w-25">Provider:</td><td>${ChatMessage._escapeHtml(stepProvider)}</td></tr>
                                                <tr><td class="text-muted">Model:</td><td>${ChatMessage._escapeHtml(stepModel)}</td></tr>
                                                <tr><td class="text-muted">Input:</td><td>${stepUsage.input.toLocaleString()} tokens</td></tr>
                                                <tr><td class="text-muted">Cached:</td><td>${stepUsage.cached.toLocaleString()} tokens</td></tr>
                                                <tr><td class="text-muted">Output:</td><td>${stepUsage.output.toLocaleString()} tokens${stepUsage.reasoning > 0 ? ` <span class="text-secondary">(incl. ${stepUsage.reasoning.toLocaleString()} reasoning)</span>` : ''}</td></tr>
                                                <tr><td class="text-muted">Total:</td><td>${stepUsage.total.toLocaleString()} tokens</td></tr>
                                                <tr><td class="text-muted">Duration:</td><td>${stepDuration} ms</td></tr>
                                                <tr><td class="text-muted">Cost:</td><td>${stepCostStr}</td></tr>
                                                <tr><td class="text-muted">Caller Type:</td><td>${ChatMessage._escapeHtml(fullTrace.callerType || 'direct')}</td></tr>
                                            </tbody>
                                        </table>
                                        ${blueprintHtml}
                                        <details class="mt-2">
                                            <summary class="text-muted small cursor-pointer mb-2">View Raw Step JSON</summary>
                                            <pre class="bg-body-tertiary border rounded p-2 small mb-0" style="white-space: pre-wrap; font-size: var(--ab-text-xs);">${ChatMessage._escapeHtml(JSON.stringify(fullTrace, null, 2))}</pre>
                                        </details>
                                    `;
                                    loadingEl.classList.add('d-none');
                                    contentEl.classList.remove('d-none');
                                }
                            } catch (err) {
                                loadingEl.innerHTML = `<span class="text-danger">Error: ${ChatMessage._escapeHtml(err.message)}</span>`;
                            }
                        }
                    }
                }
            }
        });

        const loadTurn = async () => {
            if (state.turn) return state.turn;
            const traceIds = msg.metadata?.traceIds || [];
            if (traceIds.length > 0) {
                try {
                    state.turn = await context.appClient.rpcClient.call('AITelemetryRpc.getTrace', { traceIds, excludePayloads: true });
                    return state.turn;
                } catch (err) {
                    state.turn = { error: err.message, metadata: msg.metadata || {} };
                    return state.turn;
                }
            }
            state.turn = { metadata: msg.metadata || {}, note: 'No telemetry trace is linked to this message yet.' };
            return state.turn;
        };
        const loadChat = async () => {
            if (state.chat) return state.chat;
            const threadId = context.workspace?.currentThreadId;
            if (!threadId) {
                state.chat = { note: 'No active thread id.' };
                return state.chat;
            }
            try {
                state.chat = await context.appClient.rpcClient.call('AITelemetryRpc.getStats', { filter: { threadId } });
            } catch (err) {
                state.chat = { error: err.message };
            }
            return state.chat;
        };
        const render = async (tab) => {
            content.innerHTML = '<div class="text-muted small">Loading...</div>';
            const data = tab === 'chat' ? await loadChat() : await loadTurn();

            if (data && data.error) {
                content.innerHTML = `<div class="alert alert-danger mb-0">${ChatMessage._escapeHtml(data.error)}</div>`;
                return;
            }

            if (tab === 'chat') {
                const totalTraces = data.totalTraces || 0;
                const totalTokens = data.totalTokens || 0;
                const totalCachedTokens = data.totalCachedTokens || 0;
                const totalInputTokens = data.totalInputTokens || 0;
                const totalOutputTokens = data.totalOutputTokens || 0;
                const totalReasoningTokens = data.totalReasoningTokens || 0;
                const totalCost = data.totalCost || 0;
                const errorCount = data.errorCount || 0;
                const avgDurationMs = data.avgDurationMs || 0;
                const cacheHitRate = data.cacheHitRate || 0;

                content.innerHTML = `
                    <div class="row g-3 mb-4">
                        <div class="col-6 col-md-4">
                            <div class="border rounded p-3 text-center bg-body-tertiary">
                                <div class="text-muted small mb-1">Total Interactions</div>
                                <div class="fs-4 fw-semibold">${totalTraces}</div>
                            </div>
                        </div>
                        <div class="col-6 col-md-4">
                            <div class="border rounded p-3 text-center bg-body-tertiary">
                                <div class="text-muted small mb-1">Input Tokens</div>
                                <div class="fs-4 fw-semibold">${totalInputTokens.toLocaleString()}</div>
                            </div>
                        </div>
                        <div class="col-6 col-md-4">
                            <div class="border rounded p-3 text-center bg-body-tertiary">
                                <div class="text-muted small mb-1">Avg Duration</div>
                                <div class="fs-4 fw-semibold">${(avgDurationMs / 1000).toFixed(2)}s</div>
                            </div>
                        </div>
                        <div class="col-6 col-md-4">
                            <div class="border rounded p-3 text-center bg-body-tertiary">
                                <div class="text-muted small mb-1">Cached Tokens</div>
                                <div class="fs-4 fw-semibold">${totalCachedTokens.toLocaleString()}</div>
                            </div>
                        </div>
                        <div class="col-6 col-md-4">
                            <div class="border rounded p-3 text-center bg-body-tertiary">
                                <div class="text-muted small mb-1">Output Tokens</div>
                                <div class="fs-4 fw-semibold" style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;" title="${totalOutputTokens.toLocaleString()} output tokens${totalReasoningTokens > 0 ? `, including ${totalReasoningTokens.toLocaleString()} reasoning` : ''}">${totalOutputTokens.toLocaleString()}${totalReasoningTokens > 0 ? ` <span style="font-size:var(--ab-text-xs); font-weight:normal;" class="text-secondary">(incl. ${totalReasoningTokens.toLocaleString()} reasoning)</span>` : ''}</div>
                            </div>
                        </div>
                        <div class="col-6 col-md-4">
                            <div class="border rounded p-3 text-center bg-body-tertiary">
                                <div class="text-muted small mb-1">Total Tokens</div>
                                <div class="fs-4 fw-semibold">${totalTokens.toLocaleString()}</div>
                            </div>
                        </div>
                        <div class="col-6 col-md-4">
                            <div class="border rounded p-3 text-center bg-body-tertiary">
                                <div class="text-muted small mb-1">Cache Hit Rate</div>
                                <div class="fs-4 fw-semibold">${cacheHitRate}%</div>
                            </div>
                        </div>
                        <div class="col-6 col-md-4">
                            <div class="border rounded p-3 text-center bg-body-tertiary">
                                <div class="text-muted small mb-1">Cost</div>
                                <div class="fs-4 fw-semibold">${ChatMessage._formatCost(totalCost)}</div>
                            </div>
                        </div>
                    </div>
                    <details>
                        <summary class="text-muted small cursor-pointer mb-2">View Raw Stats JSON</summary>
                        <pre class="bg-body-tertiary border rounded p-3 small mb-0" style="white-space: pre-wrap;">${ChatMessage._escapeHtml(JSON.stringify(data, null, 2))}</pre>
                    </details>
                `;
            } else {
                if (data.note) {
                    content.innerHTML = `<div class="text-muted small mb-3">${ChatMessage._escapeHtml(data.note)}</div>`;
                    if (data.metadata) {
                        content.innerHTML += `<pre class="bg-body-tertiary border rounded p-3 small mb-0" style="white-space: pre-wrap;">${ChatMessage._escapeHtml(JSON.stringify(data.metadata, null, 2))}</pre>`;
                    }
                    return;
                }

                const traces = Array.isArray(data) ? data : [data];
                let totalCost = 0;
                let totalTokens = 0;
                let totalCached = 0;
                let totalInput = 0;
                let totalOutput = 0;
                let totalDuration = 0;

                traces.forEach(t => {
                    const usage = ChatMessage._usageBreakdown(t.usage);
                    totalCost += ChatMessage._traceCost(t);
                    totalTokens += usage.total;
                    totalCached += usage.cached;
                    totalInput += usage.input;
                    totalOutput += usage.output;
                    totalDuration += t.durationMs || 0;
                });

                const costStr = ChatMessage._formatCost(totalCost);

                const nodes = traces.map((trace, index) => ({ trace, index }));

                let html = `
                    <div class="row g-3 mb-4">
                        <div class="col-6 col-md-3">
                            <div class="border rounded p-3 text-center bg-body-tertiary">
                                <div class="text-muted small mb-1">Total Cost</div>
                                <div class="fs-4 fw-semibold">${costStr}</div>
                            </div>
                        </div>
                        <div class="col-6 col-md-3">
                            <div class="border rounded p-3 text-center bg-body-tertiary">
                                <div class="text-muted small mb-1">Input Tokens</div>
                                <div class="fs-4 fw-semibold">${totalInput.toLocaleString()}</div>
                            </div>
                        </div>
                        <div class="col-6 col-md-3">
                            <div class="border rounded p-3 text-center bg-body-tertiary">
                                <div class="text-muted small mb-1">Cached Tokens</div>
                                <div class="fs-4 fw-semibold">${totalCached.toLocaleString()}</div>
                            </div>
                        </div>
                        <div class="col-6 col-md-3">
                            <div class="border rounded p-3 text-center bg-body-tertiary">
                                <div class="text-muted small mb-1">Output Tokens</div>
                                <div class="fs-4 fw-semibold">${totalOutput.toLocaleString()}</div>
                            </div>
                        </div>
                        <div class="col-6 col-md-3">
                            <div class="border rounded p-3 text-center bg-body-tertiary">
                                <div class="text-muted small mb-1">Total Tokens</div>
                                <div class="fs-4 fw-semibold">${totalTokens.toLocaleString()}</div>
                            </div>
                        </div>
                        <div class="col-6 col-md-3">
                            <div class="border rounded p-3 text-center bg-body-tertiary">
                                <div class="text-muted small mb-1">Duration</div>
                                <div class="fs-4 fw-semibold">${(totalDuration / 1000).toFixed(2)}s</div>
                            </div>
                        </div>
                        <div class="col-6 col-md-3">
                            <div class="border rounded p-3 text-center bg-body-tertiary">
                                <div class="text-muted small mb-1">Steps</div>
                                <div class="fs-4 fw-semibold">${traces.length}</div>
                            </div>
                        </div>
                    </div>

                    <h6 class="mb-3">Execution Steps</h6>
                    <div class="accordion mb-3" id="statsStepsAccordion">
                `;

                nodes.forEach((node) => {
                    const trace = node.trace;
                    const idx = node.index;
                    const stepCostStr = ChatMessage._formatCost(ChatMessage._traceCost(trace));
                    const stepUsage = ChatMessage._usageBreakdown(trace.usage);
                    const stepDuration = trace.durationMs || 0;
                    const stepStatus = trace.status || 'success';
                    const stepModel = trace.modelIdentifier || 'unknown';
                    const stepProvider = trace.providerName || 'unknown';

                    const stepHeaderId = `stepHead-${idx}`;
                    const stepCollapseId = `stepCollapse-${idx}`;

                    html += `
                        <div class="accordion-item bg-transparent border-translucent" data-trace-id="${trace.traceId}">
                            <h2 class="accordion-header" id="${stepHeaderId}">
                                <button class="accordion-button collapsed py-2 px-3 small" type="button" data-bs-toggle="collapse" data-bs-target="#${stepCollapseId}" aria-expanded="false" aria-controls="${stepCollapseId}">
                                    <div class="d-flex w-100 justify-content-between align-items-center me-3">
                                        <div>
                                            <strong>Step ${idx + 1}:</strong> ${ChatMessage._escapeHtml(stepModel)} (${ChatMessage._escapeHtml(stepProvider)})
                                        </div>
                                        <div class="small text-muted">
                                            <span class="badge ${stepStatus === 'success' ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'} me-2">${stepStatus}</span>
                                            <span class="me-2">in ${stepUsage.input.toLocaleString()}</span>
                                            <span class="me-2">cached ${stepUsage.cached.toLocaleString()}</span>
                                            <span class="me-2">out ${stepUsage.output.toLocaleString()}${stepUsage.reasoning > 0 ? ` (${stepUsage.reasoning.toLocaleString()} reasoning)` : ''}</span>
                                            <span class="me-2">${stepDuration}ms</span>
                                            <span>${stepCostStr}</span>
                                        </div>
                                    </div>
                                </button>
                            </h2>
                            <div id="${stepCollapseId}" class="accordion-collapse collapse" aria-labelledby="${stepHeaderId}">
                                <div class="accordion-body p-3">
                                    <div class="step-details-loading text-muted small"><i class="bi bi-arrow-repeat spin"></i> Loading details...</div>
                                    <div class="step-details-content d-none"></div>
                                </div>
                            </div>
                        </div>
                    `;
                });

                html += `
                    </div>
                `;

                content.innerHTML = html;
            }
        };
        modal.querySelectorAll('[data-tab]').forEach(btn => {
            btn.addEventListener('click', () => {
                modal.querySelectorAll('.nav-link').forEach(el => el.classList.remove('active'));
                btn.classList.add('active');
                render(btn.dataset.tab);
            });
        });
        await render('turn');
    }

    static _renderSystem(msg, context) {
        const div = document.createElement('div');
        div.className = 'ws-system-msg mb-3';
        div.innerHTML = `<i class="bi bi-gear-fill me-1"></i> ${ChatMessage._escapeHtml(msg.content)}`;
        if (msg.metadata?.reason === 'recursion_limit' && context?.workspace) {
            const btn = document.createElement('button');
            btn.className = 'btn btn-sm btn-outline-primary ms-2';
            btn.innerHTML = '<i class="bi bi-play-fill me-1"></i> Resume';
            btn.addEventListener('click', () => {
                context.workspace.sendMessage('Resume from the recursion limit and continue from the last completed tool result.');
            });
            div.appendChild(btn);
        }
        return div;
    }

    static _renderToolGroup(msg, context) {
        const tools = msg.metadata?.tools || [];
        const wrapper = document.createElement('div');
        wrapper.className = 'ws-msg-wrapper assistant w-100';
        if (tools.length === 0) return wrapper;

        const group = new DirectToolGroup({
            instanceId: `dtg-hist-${msg._id || Date.now()}-${Math.random().toString(36).slice(2)}`,
            appClient: context.appClient,
            workspace: context.workspace,
            tools,
            isOpen: false,
            threadId: context.workspace.currentThreadId,
        });
        group.render(wrapper);
        group.markComplete();
        return wrapper;
    }

    static _renderPresentation(msg, context) {
        const wrapper = document.createElement('div');
        wrapper.className = 'ws-msg-wrapper assistant w-100';
        
        let agentName = msg.metadata?.agentName || context.workspace.currentAgentName;
        
        const card = new OutputPresentationCard({
            instanceId: `opc-hist-${msg._id || Date.now()}`,
            appClient: context.appClient,
            workspace: context.workspace,
            ...msg.metadata,
            agentName,
            threadId: context.workspace.currentThreadId,
            projectId: context.workspace.currentProjectId,
        });
        card.render(wrapper);
        return wrapper;
    }

    static _renderSkill(msg, context) {
        const jobId = msg.metadata?.jobId;
        const wrapper = document.createElement('div');
        wrapper.className = 'ws-msg-wrapper system w-100';
        if (jobId) wrapper.id = `job-wrapper-${jobId}`;

        if (jobId && context.createManagedComponent) {
            // Rendered async
            const cardPromise = context.createManagedComponent('ai-workspace/SkillExecutionCard', wrapper, {
                instanceId: `skill-${jobId}`,
                jobId: jobId,
                goal: msg.content,
                isHistoryLoad: context.isHistoryLoad
            });

            if (context.onSkillCardCreated) {
                context.onSkillCardCreated(jobId, cardPromise);
            }
        }
        return wrapper;
    }

    static _renderEvent(msg, context) {
        const wrapper = document.createElement('div');
        wrapper.className = 'ws-system-msg mb-3';
        let text = msg.content || 'System event';
        if (msg.metadata?.eventType === 'member_added') {
            text = `${msg.metadata.targetId} joined the conversation.`;
        } else if (msg.metadata?.eventType === 'member_removed') {
            text = `${msg.metadata.targetId} left the conversation.`;
        }
        wrapper.innerHTML = `<em>${ChatMessage._escapeHtml(text)}</em>`;
        return wrapper;
    }

    static _escapeHtml(unsafe) {
        if (!unsafe) return '';
        return unsafe
             .replace(/&/g, "&amp;")
             .replace(/</g, "&lt;")
             .replace(/>/g, "&gt;")
             .replace(/"/g, "&quot;")
             .replace(/'/g, "&#039;");
    }

    static _usageBreakdown(usage = {}) {
        const cached = usage?.cached_tokens ?? usage?.cache_read_input_tokens ?? 0;
        const input = usage?.uncached_input_tokens
            ?? Math.max((usage?.prompt_tokens ?? usage?.input_tokens ?? 0) - cached, 0);
        const output = usage?.completion_tokens ?? usage?.output_tokens ?? 0;
        const reasoning = usage?.reasoning_tokens ?? usage?.completion_tokens_details?.reasoning_tokens ?? 0;
        const total = usage?.total_tokens ?? (input + cached + output);
        return { input, cached, output, total, reasoning };
    }

    static _traceCost(trace = {}) {
        return trace.platformBillableCost ?? trace.calculatedCost ?? trace.cost ?? 0;
    }

    static _formatCost(cost = 0) {
        return cost > 0 ? `$${cost.toFixed(5)}` : '$0.00';
    }
}
